import React, { useState, useMemo } from "react";
import { apiFetch } from "@/lib/api";
import { toast } from "@/components/ui/feedback";
import {
  ActionPrompt,
  ActionPromptTagGroup,
  ActionPromptTag,
} from "./actionPromptTypes";
import { ActionPromptCard } from "./ActionPromptCard";
import { ActionPromptEditorModal } from "./ActionPromptEditorModal";
import { ActionPromptTagManagerModal } from "./ActionPromptTagManagerModal";

interface ActionPromptLibraryProps {
  prompts: ActionPrompt[];
  groups: ActionPromptTagGroup[];
  tags: ActionPromptTag[];
  reloadPrompts: () => void;
  reloadTagData: () => void;
}

export function ActionPromptLibrary({
  prompts,
  groups,
  tags,
  reloadPrompts,
  reloadTagData,
}: ActionPromptLibraryProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [editingPrompt, setEditingPrompt] = useState<ActionPrompt | null | undefined>(
    undefined
  ); // undefined = closed, null = create new, object = edit existing
  const [showTagManager, setShowTagManager] = useState(false);

  // Enabled groups & tags
  const enabledGroups = useMemo(() => groups.filter((g) => g.enabled), [groups]);

  const tagsForGroup = (groupId: string) => {
    return tags.filter((t) => t.enabled && t.groupId === groupId);
  };

  const toggleTagFilter = (tagId: string) => {
    setSelectedTagIds((current) =>
      current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId]
    );
  };

  const resetFilters = () => {
    setSearchQuery("");
    setSelectedTagIds([]);
  };

  const hasActiveFilters = searchQuery.trim().length > 0 || selectedTagIds.length > 0;

  // Filtered prompts
  const filteredPrompts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return prompts.filter((prompt) => {
      // 1. Text / name search filter
      if (q) {
        const matchesName = prompt.name.toLowerCase().includes(q);
        const matchesText = prompt.text.toLowerCase().includes(q);
        if (!matchesName && !matchesText) return false;
      }

      // 2. Tag filters
      if (selectedTagIds.length > 0) {
        const promptTagIds = (prompt.tags || []).map((t) => t.id);
        const hasAllSelected = selectedTagIds.every((id) =>
          promptTagIds.includes(id)
        );
        if (!hasAllSelected) return false;
      }

      return true;
    });
  }, [prompts, searchQuery, selectedTagIds]);

  // Operations
  const handleToggleEnabled = async (prompt: ActionPrompt) => {
    try {
      const res = await apiFetch(`/faceswap/action-prompts/${prompt.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !prompt.enabled }),
      });
      if (!res.ok) throw new Error(await res.text());

      toast(!prompt.enabled ? "动作提示词已启用" : "动作提示词已禁用", "success");
      reloadPrompts();
    } catch (err) {
      toast("更新状态失败：" + (err instanceof Error ? err.message : String(err)), "error");
    }
  };

  const handleDelete = async (prompt: ActionPrompt) => {
    if (!window.confirm(`确认删除动作提示词「${prompt.name}」？若已被模板关联将无法删除。`)) {
      return;
    }

    try {
      const res = await apiFetch(`/faceswap/action-prompts/${prompt.id}`, {
        method: "DELETE",
      });

      if (res.status === 409) {
        toast("删除失败：该动作已被模板关联为适用动作，无法删除。请先在相关模板中移除该动作。", "error");
        return;
      }
      if (!res.ok) throw new Error(await res.text());

      toast("动作提示词已删除", "success");
      reloadPrompts();
    } catch (err) {
      toast("删除失败：" + (err instanceof Error ? err.message : String(err)), "error");
    }
  };

  return (
    <div className="space-y-4">
      {/* Top Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">动作提示词库</h2>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">
              共 {prompts.length} 个动作
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            配置写实旅拍适用的自拍人像动作姿态。万相生成根据性别与年龄段匹配后随机追加在提示词末尾。
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowTagManager(true)}
            className="px-3.5 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-1.5 shadow-sm"
          >
            <i className="ri-price-tag-3-line text-teal-600" />
            <span>标签管理</span>
          </button>
          <button
            type="button"
            onClick={() => setEditingPrompt(null)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors flex items-center gap-1.5 shadow-sm"
          >
            <i className="ri-add-line text-base" />
            <span>新建动作提示词</span>
          </button>
        </div>
      </div>

      {/* Search & Tag Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3 shadow-sm">
        {/* Search input & reset */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索动作名称或动作提示词文本内容..."
              className="w-full pl-9 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-base" />
              </button>
            )}
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs text-gray-500 hover:text-teal-700 flex items-center gap-1 shrink-0 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
            >
              <i className="ri-refresh-line" />
              <span>清空筛选</span>
            </button>
          )}
        </div>

        {/* Tag Filters by Group */}
        {enabledGroups.length > 0 && (
          <div className="pt-2 border-t border-gray-100 space-y-2 text-xs">
            {enabledGroups.map((group) => {
              const groupTags = tagsForGroup(group.id);
              if (groupTags.length === 0) return null;

              return (
                <div key={group.id} className="flex items-center gap-2 flex-wrap">
                  <span className="text-gray-500 shrink-0 w-16 text-right font-medium">
                    {group.name}:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {groupTags.map((tag) => {
                      const isSelected = selectedTagIds.includes(tag.id);
                      return (
                        <button
                          type="button"
                          key={tag.id}
                          onClick={() => toggleTagFilter(tag.id)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                            isSelected
                              ? "bg-teal-600 text-white shadow-sm"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Grid or Empty States */}
      {prompts.length === 0 ? (
        // Entire Library is Empty
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center space-y-4 shadow-sm">
          <div className="w-16 h-16 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center mx-auto text-3xl">
            <i className="ri-walk-line" />
          </div>
          <div className="max-w-md mx-auto space-y-1.5">
            <h3 className="text-base font-semibold text-gray-900">
              暂无动作提示词
            </h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              当前动作提示词库为空。万相生成将按模板的「模板提示词 + 人物年龄性别提示词 + 固定提示词（人体约束）」正常生成，不会受阻。您可随时添加动作以增强写实人像的多样性。
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditingPrompt(null)}
            className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors inline-flex items-center gap-1.5 shadow-sm"
          >
            <i className="ri-add-line text-base" />
            <span>新建第一个动作提示词</span>
          </button>
        </div>
      ) : filteredPrompts.length === 0 ? (
        // Search / Filter matched 0
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center space-y-3 shadow-sm">
          <div className="w-12 h-12 bg-gray-100 text-gray-400 rounded-xl flex items-center justify-center mx-auto text-2xl">
            <i className="ri-filter-off-line" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-gray-900">
              未找到匹配的动作提示词
            </h3>
            <p className="text-xs text-gray-500">
              请尝试调整搜索关键词或清除部分标签筛选条件。
            </p>
          </div>
          <button
            type="button"
            onClick={resetFilters}
            className="px-3.5 py-1.5 border border-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors"
          >
            重置筛选条件
          </button>
        </div>
      ) : (
        // Cards Grid
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPrompts.map((prompt) => (
            <ActionPromptCard
              key={prompt.id}
              prompt={prompt}
              onEdit={(p) => setEditingPrompt(p)}
              onDelete={handleDelete}
              onToggleEnabled={handleToggleEnabled}
            />
          ))}
        </div>
      )}

      {/* Editor Modal (Create / Edit) */}
      {editingPrompt !== undefined && (
        <ActionPromptEditorModal
          prompt={editingPrompt}
          groups={groups}
          tags={tags}
          open={true}
          onClose={() => setEditingPrompt(undefined)}
          onSaved={() => {
            reloadPrompts();
            reloadTagData();
          }}
        />
      )}

      {/* Tag Manager Modal */}
      {showTagManager && (
        <ActionPromptTagManagerModal
          groups={groups}
          tags={tags}
          open={true}
          onClose={() => setShowTagManager(false)}
          onChanged={() => {
            reloadTagData();
            reloadPrompts();
          }}
        />
      )}
    </div>
  );
}
